export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || "G-63FTW1G9BR";

const STORAGE_KEY = "premiumTourAttribution";
const DISABLE_STORAGE_KEY = "premiumTourAnalyticsDisabled";
const DISABLE_PARAMS = ["no_ga", "disable_ga", "ga_opt_out"];

let initialized = false;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const getParam = (params, key) => params.get(key) || undefined;

const normalizeEventParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );

const updateAnalyticsDisabledFlag = () => {
  if (!isBrowser()) return false;

  const params = new URLSearchParams(window.location.search);
  const matchedParam = DISABLE_PARAMS.find((key) => params.has(key));

  if (!matchedParam) {
    return window.localStorage?.getItem(DISABLE_STORAGE_KEY) === "1";
  }

  const value = params.get(matchedParam);
  const shouldDisable = value !== "0" && value !== "false";

  if (shouldDisable) {
    window.localStorage?.setItem(DISABLE_STORAGE_KEY, "1");
  } else {
    window.localStorage?.removeItem(DISABLE_STORAGE_KEY);
  }

  return shouldDisable;
};

export const isAnalyticsDisabled = () => {
  if (!isBrowser()) return true;

  const hostname = window.location.hostname;
  const isLocalhost = ["localhost", "127.0.0.1", "::1"].includes(hostname);

  return isLocalhost || updateAnalyticsDisabledFlag();
};

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getAttribution = () => {
  if (!isBrowser()) return {};

  const params = new URLSearchParams(window.location.search);
  const current = {
    utm_source: getParam(params, "utm_source"),
    utm_medium: getParam(params, "utm_medium"),
    utm_campaign: getParam(params, "utm_campaign"),
    utm_term: getParam(params, "utm_term"),
    utm_content: getParam(params, "utm_content"),
    gclid: getParam(params, "gclid"),
    fbclid: getParam(params, "fbclid"),
    referrer: document.referrer || undefined,
    landing_page: window.location.href,
  };

  const hasCampaignData = Object.values(current).some(Boolean);
  const saved = safeJsonParse(window.localStorage?.getItem(STORAGE_KEY));

  if (!saved && hasCampaignData) {
    const firstTouch = {
      ...current,
      first_visit_at: new Date().toISOString(),
    };

    window.localStorage?.setItem(STORAGE_KEY, JSON.stringify(firstTouch));
    return firstTouch;
  }

  return saved || current;
};

const loadGtagScript = () => {
  if (
    !isBrowser() ||
    isAnalyticsDisabled() ||
    document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`)
  ) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

export const initGA = () => {
  if (!isBrowser() || initialized || !GA_MEASUREMENT_ID || isAnalyticsDisabled()) return;

  loadGtagScript();

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    user_properties: getAttribution(),
  });

  initialized = true;
};

export const logAttributionOnce = () => {
  if (!isBrowser() || isAnalyticsDisabled()) return;

  initGA();

  const sessionKey = `${STORAGE_KEY}:sent`;
  if (window.sessionStorage?.getItem(sessionKey)) return;

  const attribution = getAttribution();
  window.gtag?.("event", "traffic_attribution", attribution);
  window.sessionStorage?.setItem(sessionKey, "1");
};

export const logPageView = (path, title = document.title) => {
  if (!isBrowser() || isAnalyticsDisabled()) return;

  initGA();

  const pagePath = path || `${window.location.pathname}${window.location.search}`;

  window.gtag?.("event", "page_view", {
    page_title: title,
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    page_referrer: document.referrer || undefined,
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!isBrowser() || isAnalyticsDisabled() || !eventName) return;

  initGA();

  window.gtag?.("event", eventName, normalizeEventParams({
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title: document.title,
    ...params,
  }));
};

export const trackCtaClick = ({
  label,
  placement,
  destination,
  destinationType,
  value,
} = {}) => {
  trackEvent("cta_click", {
    event_category: "engagement",
    event_label: label,
    label,
    placement,
    destination,
    destination_type: destinationType,
    value,
  });
};

const bookingEventParams = (params = {}) => normalizeEventParams({
  event_category: "booking_funnel",
  currency: "JPY",
  ...params,
});

export const trackTourOfferView = (params = {}) =>
  trackEvent("tour_offer_view", bookingEventParams(params));

export const trackTourDetailClick = (params = {}) =>
  trackEvent("tour_detail_click", bookingEventParams(params));

export const trackLineBookingClick = (params = {}) =>
  trackEvent("line_booking_click", bookingEventParams({ booking_channel: "line", ...params }));

export const trackInstagramBookingClick = (params = {}) =>
  trackEvent("instagram_booking_click", bookingEventParams({ booking_channel: "instagram", ...params }));

export const trackPhoneBookingClick = (params = {}) =>
  trackEvent("phone_booking_click", bookingEventParams({ booking_channel: "phone", ...params }));

export const trackPartnerTourClick = (params = {}) =>
  trackEvent("partner_tour_click", bookingEventParams(params));

export const trackRestaurantMapClick = (params = {}) =>
  trackEvent("restaurant_map_click", bookingEventParams(params));

export const trackBookingInquiry = (params = {}) =>
  trackEvent("booking_inquiry", bookingEventParams(params));

export const trackBookingConfirmed = (params = {}) =>
  trackEvent("booking_confirmed", bookingEventParams(params));

export const trackPurchase = ({ transactionId, value, items = [], ...params } = {}) =>
  trackEvent("purchase", bookingEventParams({
    transaction_id: transactionId,
    value,
    items,
    ...params,
  }));
